import { useInfiniteQuery } from '@tanstack/react-query';

import { stringToDateRange } from '@/helpers';
import { FilterValues } from '@/hooks/use-filter-values';
import {
  DEFAULT_PAGE_SIZE,
  flattenInfinitePages,
  getNextPageParam,
} from '@/lib/constants/pagination';
import { normalizeToStartOfDayUTC } from '@/utils/date-formatters';

import { getTimeOffTypeChanges } from '../../api/time-off-changes.api';
import {
  TIME_OFF_CHANGES_FILTER_KEY_CHANGED_BY,
  TIME_OFF_CHANGES_FILTER_KEY_DATE_RANGE,
} from '../../constants/time-off-changes-filter-keys';

export const TIME_OFF_TYPE_CHANGES_QUERY_KEY_ROOT = ['time-off-type-changes'] as const;
export const TIME_OFF_TYPE_CHANGES_QUERY_KEY = (typeId: number, filters: FilterValues['values']) =>
  [...TIME_OFF_TYPE_CHANGES_QUERY_KEY_ROOT, typeId, filters] as const;

interface TimeOffTypeChangesApiFilters {
  fromDate?: string;
  toDate?: string;
  changedByUserIds?: number[];
}

/**
 * Maps UI filter values to the time-off changes API request shape.
 */
const mapTimeOffChangesFilters = (
  filters: FilterValues['values']
): TimeOffTypeChangesApiFilters => {
  const apiFilters: TimeOffTypeChangesApiFilters = {};

  const dateRange = filters[TIME_OFF_CHANGES_FILTER_KEY_DATE_RANGE];
  if (dateRange) {
    const range = stringToDateRange(dateRange as string);
    if (range?.from) apiFilters.fromDate = normalizeToStartOfDayUTC(range.from).toISOString();
    if (range?.to) apiFilters.toDate = normalizeToStartOfDayUTC(range.to).toISOString();
  }

  const changedBy = filters[TIME_OFF_CHANGES_FILTER_KEY_CHANGED_BY];
  if (changedBy) apiFilters.changedByUserIds = [Number(changedBy)];

  return apiFilters;
};

interface UseTimeOffTypeChangesParams {
  typeId: number;
  filters?: FilterValues['values'];
  enabled?: boolean;
}

/**
 * Fetches the scheduled changes for a time-off type with cursor pagination and filtering.
 */
export const useTimeOffTypeChanges = ({
  typeId,
  filters = {},
  enabled = true,
}: UseTimeOffTypeChangesParams) =>
  useInfiniteQuery({
    queryKey: TIME_OFF_TYPE_CHANGES_QUERY_KEY(typeId, filters),
    queryFn: ({ pageParam }) =>
      getTimeOffTypeChanges({
        typeId,
        ...mapTimeOffChangesFilters(filters),
        pagination: { pageSize: DEFAULT_PAGE_SIZE, cursor: pageParam },
      }),
    getNextPageParam,
    initialPageParam: undefined as number | undefined,
    enabled,
    // Changes don't update frequently during a session.
    staleTime: 5 * 60 * 1000,
    select: flattenInfinitePages,
  });
